import m from "mithril"
import { memoize } from "oj-utils"
import { IRect, IDimensions, PositionMutation, ISnapPoint, IPoint } from "./entries/editor/types"

export const getImageTypeFromB64 = (source: string) => {
  const match = source.match(/(image\/.*)?;/)
  return (match && match[1]) ? match[1] : "image/png"
}

export const base64toBlob = (src: string) => {
  const base64Data = src.replace(/^data:image\/(.*?);base64,/, "")
  const contentType = getImageTypeFromB64(src)
  const sliceSize = 1024
  const byteCharacters = atob(base64Data)
  const bytesLength = byteCharacters.length
  const slicesCount = Math.ceil(bytesLength / sliceSize)
  const byteArrays = new Array(slicesCount)

  for (let i = 0; i < slicesCount; ++i) {
    const begin = i * sliceSize
    const end = Math.min(begin + sliceSize, bytesLength)
    const bytes = new Array(end - begin)
    for (let offset = begin, i = 0; offset < end; ++i, ++offset)
      bytes[i] = byteCharacters[offset].charCodeAt(0)
    byteArrays[i] = new Uint8Array(bytes)
  }
  return new Blob(byteArrays, { type: contentType })
}

export const getLocale = () =>
  m.parseQueryString(location.search).locale as string

export const getEnumArray = <T>(e: any): [string, T][] => {
  const entries = Object.entries(e)
  if (typeof entries[entries.length - 1][1] === "number")
    return entries.slice(entries.length / 2) as any
  else
    return entries as any
}

export const getEnumKeyByValue = (e: any, v: number | string) =>
  getEnumArray(e).find(x => x[1] == v)[0]

export const isIE = memoize(() =>
  (/MSIE (\d+\.\d+);/.test(navigator.userAgent) || navigator.userAgent.indexOf("Trident/") > -1),
  () => "")

export const isArabicText = (text: string) =>
  /[\u0600-\u06FF]/.test(text)

export const getIndex = (indexOrKey: number | string, data: { key: string }[]) =>
  typeof indexOrKey === "number" ? indexOrKey : data.findIndex(x => x.key === indexOrKey)

export const getKey = (indexOrKey: number | string, data: { key: string }[]) =>
  typeof indexOrKey === "string" ? indexOrKey : data[indexOrKey].key

export const constrainPosition = (newPos: IRect, sidePos: IDimensions, margin: IPoint, type: PositionMutation) => {
  if (type === "all") {
    let tmp = constrainPosition(newPos, sidePos, margin, "move")
    tmp = constrainPosition(tmp, sidePos, margin, "resize-top-left")
    tmp = constrainPosition(tmp, sidePos, margin, "resize-top-right")
    tmp = constrainPosition(tmp, sidePos, margin, "resize-bottom-left")
    tmp = constrainPosition(tmp, sidePos, margin, "resize-bottom-right")
    return tmp
  }

  const next = { ...newPos }

  if (type === "move") {
    next.width = Math.min(sidePos.width, Math.max(20, next.width))
    next.height = Math.min(sidePos.height, Math.max(20, next.height))
    next.x = Math.min(sidePos.width - next.width - margin.x, Math.max(margin.x, next.x))
    next.y = Math.min(sidePos.height - next.height - margin.y, Math.max(margin.y, next.y))
  }

  if (type.indexOf("left") !== -1) {
    if (next.x < margin.x) {
      const diff = margin.x - next.x
      next.x = margin.x
      next.width -= diff
    }
    if (next.width < 20) {
      const diff = 20 - next.width
      next.width = 20
      next.x -= diff
    }
  }

  if (type.indexOf("right") !== -1) {
    if (next.x + next.width > sidePos.width - margin.x)
      next.width = sidePos.width - next.x - margin.x
    if (next.width < 20)
      next.width = 20
  }

  if (type.indexOf("top") !== -1) {
    if (next.y < margin.y) {
      const diff = margin.y - next.y
      next.y = margin.y
      next.height -= diff
    }
    if (next.height < 20) {
      const diff = 20 - next.height
      next.height = 20
      next.y -= diff
    }
  }

  if (type.indexOf("bottom") !== -1) {
    if (next.y + next.height > sidePos.height - margin.y)
      next.height = sidePos.height - next.y - margin.y
    if (next.height < 20)
      next.height = 20
  }

  return next
}

export const snapPosition = (position: IRect, points: ISnapPoint[], onsnap?: (points: ISnapPoint[]) => void, force?: boolean) => {
  const nextPos = { ...position }

  const calculated: { diff: number, pos: number, i: number }[][] = [[], []]

  for (let i = 0; i < points.length; i++) {
    const point = points[i]
    const { x, y } = checkSnapPoint(position, point, force ? Infinity : 4)
    if (x) calculated[0].push({ ...x, i })
    if (y) calculated[1].push({ ...y, i })
  }

  const closest = calculated.map(x => x.sort((a, b) => a.diff - b.diff)[0])
  if (closest[0])
    nextPos.x = closest[0].pos
  if (closest[1])
    nextPos.y = closest[1].pos

  if (onsnap)
    onsnap(closest.filter(x => x).map(x => points[x.i]))

  return nextPos
}

export const checkSnapPoint = (position: IRect, point: ISnapPoint, threshold: number = 4) => {
  let x: { diff: number, pos: number }
  let y: { diff: number, pos: number }

  if (point.x) {
    let pos = position.x + (position.width * point.snap)
    let diff = point.x - pos
    if (Math.abs(diff) < threshold)
      x = { diff, pos: (pos - (position.width * point.snap)) + diff }
  }
  if (point.y) {
    let pos = position.y + (position.height * point.snap)
    let diff = point.y - pos
    if (Math.abs(diff) < threshold)
      y = { diff, pos: (pos - (position.height * point.snap)) + diff }
  }

  return { x, y }
}